import FormFieldId from '../../../types/FormFieldId';
import globalTranslations from '../../../i18n/globalTranslations';

/** Plain strings for intl interpolation (labels.* from formLabels are React elements). */
function formatDraftFieldLabel(intl, fieldId) {
  switch (fieldId) {
    case FormFieldId.DELIVERY_COMPANY:
      return intl.formatMessage(globalTranslations.companyTitle);
    case FormFieldId.DELIVERY_FIRST_NAME:
      return intl.formatMessage(globalTranslations.firstNameTitle);
    case FormFieldId.DELIVERY_LAST_NAME:
      return intl.formatMessage(globalTranslations.lastNameTitle);
    case FormFieldId.DELIVERY_STREET_ADDRESS:
      return intl.formatMessage(globalTranslations.streetAddressTitle);
    case FormFieldId.DELIVERY_POSTAL_CODE:
      return intl.formatMessage(globalTranslations.zipCodeTitle);
    case FormFieldId.DELIVERY_CITY:
      return intl.formatMessage(globalTranslations.cityTitle);
    case FormFieldId.DELIVERY_COUNTRY:
      return intl.formatMessage(globalTranslations.countryTitle);
    case FormFieldId.DELIVERY_PHONE:
      return intl.formatMessage(globalTranslations.telephoneTitle);
    case FormFieldId.DELIVERY_CARE_OF:
      return intl.formatMessage(globalTranslations.careOfTitle);
    default:
      return intl.formatMessage(globalTranslations.streetAddressTitle);
  }
}

export function createEmptyManualDeliveryDraft() {
  return {
    deliveryIsCompany: false,
    company: '',
    firstname: '',
    lastname: '',
    streetAddress: '',
    postalCode: '',
    city: '',
    country: '',
    telephone: '',
    careOf: '',
    saveToAddressBook: true,
  };
}

function requiredMessage(intl, fieldLabel) {
  return intl.formatMessage(
    {
      id: 'validation.required',
      defaultMessage: '{field} cannot be blank.',
    },
    { field: fieldLabel }
  );
}

function minLenMessage(intl, fieldLabel, minLength) {
  return intl.formatMessage(
    {
      id: 'validation.minLength',
      defaultMessage: '{field} must have at least {minLength} characters.',
    },
    { field: fieldLabel, minLength }
  );
}

function maxLenMessage(intl, fieldLabel, maxLength) {
  return intl.formatMessage(
    {
      id: 'validation.maxLength',
      defaultMessage: '{field} must not have more than {maxLength} characters.',
    },
    { field: fieldLabel, maxLength }
  );
}

export function getManualDraftFieldValue(draft, fieldId) {
  switch (fieldId) {
    case FormFieldId.DELIVERY_COMPANY:
      return draft.company;
    case FormFieldId.DELIVERY_FIRST_NAME:
      return draft.firstname;
    case FormFieldId.DELIVERY_LAST_NAME:
      return draft.lastname;
    case FormFieldId.DELIVERY_STREET_ADDRESS:
      return draft.streetAddress;
    case FormFieldId.DELIVERY_POSTAL_CODE:
      return draft.postalCode;
    case FormFieldId.DELIVERY_CITY:
      return draft.city;
    case FormFieldId.DELIVERY_COUNTRY:
      return draft.country;
    case FormFieldId.DELIVERY_PHONE:
      return draft.telephone;
    case FormFieldId.DELIVERY_CARE_OF:
      return draft.careOf;
    default:
      return '';
  }
}

function validateSingleDraftFieldFromMeta(
  fieldMeta,
  fieldId,
  draft,
  intl,
  isCo,
  fieldErrors
) {
  if (!fieldMeta) return;

  let required = Boolean(fieldMeta.required);
  if (
    fieldId === FormFieldId.DELIVERY_FIRST_NAME ||
    fieldId === FormFieldId.DELIVERY_LAST_NAME
  ) {
    required = required && !isCo;
  }

  const label = formatDraftFieldLabel(intl, fieldId);
  const raw = getManualDraftFieldValue(draft, fieldId);
  const str = raw == null ? '' : String(raw);

  if (required && !str.trim()) {
    fieldErrors[fieldId] = requiredMessage(intl, label);
    return;
  }

  if (
    fieldMeta.min_length &&
    str.trim() &&
    str.trim().length < fieldMeta.min_length
  ) {
    fieldErrors[fieldId] = minLenMessage(intl, label, fieldMeta.min_length);
    return;
  }

  if (
    fieldMeta.max_length &&
    str &&
    String(str).trim().length > fieldMeta.max_length
  ) {
    fieldErrors[fieldId] = maxLenMessage(intl, label, fieldMeta.max_length);
  }
}

/**
 * Validates manual delivery draft the same way CheckoutDeliveryForm treats required
 * fields (consumer vs company for first/last name).
 *
 * @param {object} [order] Company on the order is validated when missing from
 *   `deliveryForm` (e.g. B2C + address book dropdown-only form).
 * @returns {{ valid: boolean, fieldErrors: Record<string, string> }}
 */
export function validateManualDeliveryDraft(deliveryForm, draft, intl, order) {
  const fieldErrors = {};
  if (!deliveryForm?.hasFields) {
    return { valid: true, fieldErrors };
  }

  const isCo = Boolean(draft.deliveryIsCompany);

  const formFieldIds = new Set(
    deliveryForm.fieldsArray.map((f) => f.id).filter(Boolean)
  );

  deliveryForm.fieldsArray.forEach((field) => {
    if (
      field.id === FormFieldId.DELIVERY_ADDRESS_DROPDOWN ||
      field.id === FormFieldId.DELIVERY_IS_COMPANY
    ) {
      return;
    }

    if (field.id === FormFieldId.DELIVERY_COMPANY) {
      if (!isCo) {
        return;
      }
    } else if (!field.active) {
      return;
    }

    validateSingleDraftFieldFromMeta(
      field,
      field.id,
      draft,
      intl,
      isCo,
      fieldErrors
    );
  });

  if (isCo) {
    const fid = FormFieldId.DELIVERY_COMPANY;
    if (!formFieldIds.has(fid)) {
      const orderField =
        order?.deliveryFields?.find((f) => f.id === fid) ??
        order?.customer_fields?.get(fid);
      if (orderField) {
        validateSingleDraftFieldFromMeta(
          orderField,
          fid,
          draft,
          intl,
          isCo,
          fieldErrors
        );
      } else {
        validateSingleDraftFieldFromMeta(
          { required: true },
          fid,
          draft,
          intl,
          isCo,
          fieldErrors
        );
      }
    }
  }

  return {
    valid: Object.keys(fieldErrors).length === 0,
    fieldErrors,
  };
}

export function setDraftValueFromFieldId(draft, fieldId, value) {
  const v = value == null ? '' : value;
  switch (fieldId) {
    case FormFieldId.DELIVERY_COMPANY:
      return { ...draft, company: v };
    case FormFieldId.DELIVERY_FIRST_NAME:
      return { ...draft, firstname: v };
    case FormFieldId.DELIVERY_LAST_NAME:
      return { ...draft, lastname: v };
    case FormFieldId.DELIVERY_STREET_ADDRESS:
      return { ...draft, streetAddress: v };
    case FormFieldId.DELIVERY_POSTAL_CODE:
      return { ...draft, postalCode: v };
    case FormFieldId.DELIVERY_CITY:
      return { ...draft, city: v };
    case FormFieldId.DELIVERY_COUNTRY:
      return { ...draft, country: v === '' ? '' : String(v) };
    case FormFieldId.DELIVERY_PHONE:
      return { ...draft, telephone: v };
    case FormFieldId.DELIVERY_CARE_OF:
      return { ...draft, careOf: v };
    default:
      return draft;
  }
}

export function draftCountryToOrderValue(countryStr) {
  if (countryStr == null || countryStr === '') {
    return null;
  }
  const asNum = Number(countryStr);
  return Number.isNaN(asNum) ? String(countryStr) : asNum;
}
